import moment from 'moment';
import i18next from 'i18next';
import { Area } from '@antv/g2plot';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import emptyData from './hardcoded';
import { renderTooltip } from '../../../../helpers/Tooltip';
import { getRange } from '../../../Modules/pme/screens/dashboard/widgets/mitigation-actions-trend/helper';

const colors = [
  { value: '#2E90FA', key: 'inclusiveEconomicDevelopment' },
  { value: '#6AD99E', key: 'environmentalProtection' },
  { value: '#6C737F', key: 'safeAndDignifiedWorkingConditions' },
  { value: '#FEC84B', key: 'goodGovernance' },
];

const selectFilters = ({
  theme: {
    type: 'select',
    label: '',
    placeholder: (t) => t('Custom Theme'),
    style: { flex: 1 },
    labelStyle: { flex: 1 },
    isMulti: true,
  },
})

export default function useContinuousImprovement({ container }) {
  const { t } = useTranslation();
  const [graph, setGraph] = useState(null);
  const [filters, setFilters] = useState({});

  const data = useMemo(() => emptyData, []);

  const filtersConfig = useMemo(() => ({
    language: i18next.language,
    filtersConfig: selectFilters,
    onApply: (val) => setFilters(val),
    options: {
      theme: [
        { label: t('Inclusive Economic Development'), value: 'inclusiveEconomicDevelopment' },
        { label: t('Environmental Protection'), value: 'environmentalProtection' },
        { label: t('Safe and Dignified Working Conditions'), value: 'safeAndDignifiedWorkingConditions' },
        { label: t('Good Governance'), value: 'goodGovernance' },
      ],
    },
    t,
  }), [t, selectFilters, i18next.language]);

  useEffect(() => {
    let _data = Array.isArray(filters.theme) && filters.theme.length
      ? data.filter((d) => filters.theme.includes(d.series))
      : data;

    const _colors = Array.isArray(filters.theme) && filters.theme.length
      ? colors.filter((v) => filters.theme.includes(v.key)).map((v) => v.value)
      : colors.map((v) => v.value);

    _data = _data.map((d) => {
      const val = filtersConfig.options.theme.find((th) => th.value === d.series);
      return { ...d, series: val?.label || d.series };
    })

    if (!graph) {
      const area = new Area(container.current, {
        data: _data || [],
        xField: "type",
        yField: "value",
        seriesField: "series",
        legend: false,
        xAxis: {
          label: {
            "style": {
              "fontSize": 10
            }
          }
        },
        tooltip: {
          customContent: (title, data) => {
            let content = '';

            if (data && data.length) {
              content = renderTooltip({
                title: t('Continuous Improvement'),
                subTitle: moment((data[0]?.title || '').split('M')[1], 'M').format('MMMM'),
                items: data.map((d) => ({
                  color: d.mappingData.color,
                  label: t(d.name),
                  value: d.value ? Number(d.value) : '-',
                }))
              })
            }

            return content;
          }
        },
        yAxis: {
          tickMethod: (a) => getRange(a.max),
        },
        color: _colors,
      });

      setGraph(area);
      area.render();
    } else if (graph) {
      graph.update({
        data: _data,
        color: _colors,
        animation: false,
        tooltip: {
          customContent: (title, data) => {
            let content = '';

            if (data && data.length) {
              content = renderTooltip({
                title: t('Continuous Improvement'),
                subTitle: moment((data[0]?.title || '').split('M')[1], 'M').format('MMMM'),
                items: data.map((d) => ({
                  color: d.mappingData.color,
                  label: t(d.name),
                  value: d.value ? Number(d.value) : '-',
                }))
              })
            }

            return content;
          }
        },
      });
    }
  }, [data, t, filters, filtersConfig]);

  return { filtersConfig };
}
